import { format, parse } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import type { UserConfig } from '@/slices/userConfigSlice';

// Detects ISO-like date strings (date or datetime)
const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
const isoDateTimePrefixRegex = /^\d{4}-\d{2}-\d{2}[T\s]/;

// Detects DDMonYY like 05Aug25 (mon in English short month names)
const ddMonYYRegex = /^\d{2}[A-Za-z]{3}\d{2}$/;

export type ParsedDateResult = {
  formatted: string;
  date: Date | null;
  matched: boolean;
};

function toZoned(date: Date, timezone: string) {
  try {
    return toZonedTime(date, timezone);
  } catch {
    return date;
  }
}

export function parseAndFormatUserDateString(
  value: string,
  userConfig: UserConfig
): ParsedDateResult {
  const dateFormat = userConfig?.dateFormat?.value || 'dd-MMM-yy';
  const timezone = userConfig?.timezone?.value || 'UTC';

  // ISO date handling
  if (isoDateRegex.test(value) || isoDateTimePrefixRegex.test(value)) {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) {
      const zoned = toZoned(d, timezone);
      return { formatted: format(zoned, dateFormat), date: zoned, matched: true };
    }
  }

  // DDMonYY handling e.g., 05Aug25
  if (ddMonYYRegex.test(value)) {
    // Parse with a fixed UTC-like baseline to avoid local timezone shifting the date
    const baseline = new Date(Date.UTC(2000, 0, 1, 12, 0, 0)); // midday UTC to avoid DST quirks
    const parsed = parse(value, 'ddMMMyy', baseline);
    if (!Number.isNaN(parsed.getTime())) {
      // Treat parsed as UTC date (date-only). Create a pure date using its Y/M/D in UTC
      const y = parsed.getFullYear();
      const m = parsed.getMonth();
      const dNum = parsed.getDate();
      const asUtcDate = new Date(Date.UTC(y, m, dNum, 0, 0, 0));
      const zoned = toZoned(asUtcDate, timezone);
      return { formatted: format(zoned, dateFormat), date: zoned, matched: true };
    }
  }

  return { formatted: value, date: null, matched: false };
}
